import { motion } from 'framer-motion'
import { ArrowRight, Play, CheckCircle, ChevronDown } from 'lucide-react'

export default function Hero() {
  const scrollToNext = () => {
    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <section className="relative min-h-[85vh] flex items-center bg-gradient-to-br from-slate-50 via-white to-blue-50 overflow-hidden">
      {/* Background decoration com animações suaves */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Bolha azul animada */}
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-100 rounded-full blur-3xl opacity-50"
          animate={{ y: [0, 30, 0], x: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Bolha violeta animada */}
        <motion.div
          className="absolute top-1/2 -left-20 w-60 h-60 bg-violet-100 rounded-full blur-3xl opacity-60"
          animate={{ y: [0, -25, 0], x: [0, 15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Bolha menor azul escuro */}
        <motion.div
          className="absolute bottom-10 left-1/3 w-32 h-32 bg-blue-300 rounded-full blur-2xl opacity-40"
          animate={{ y: [0, 20, 0], x: [0, 10, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Bolha menor violeta */}
        <motion.div
          className="absolute bottom-24 right-1/4 w-24 h-24 bg-violet-200 rounded-full blur-2xl opacity-30"
          animate={{ y: [0, -15, 0], x: [0, -10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="container-custom relative z-10 pt-28 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            {/* Badge IA */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-100 to-blue-100 border border-violet-200 mb-6"
            >
              <span className="text-lg">🤖</span>
              <span className="text-sm font-semibold bg-gradient-to-r from-violet-600 to-blue-600 bg-clip-text text-transparent">
                Potencializado por Inteligência Artificial
              </span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              Gestão{' '}
              <span className="gradient-text">Inteligente</span> para Seu Restaurante
            </h1>

            <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0">
              IA integrada em PDV, cozinha, estoque e análise de clientes. 
              Automação que reduz custos e aumenta seu faturamento.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
              <motion.a
                href="#contato"
                className="btn-primary"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                Quero conhecer o sistema
                <ArrowRight className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="#como-funciona"
                className="btn-secondary"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <Play className="w-5 h-5" />
                Ver Como Funciona
              </motion.a>
            </div>

            {/* Trust indicators - IA Features */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-sm">
              {[
                { icon: '🧠', text: 'IA Preditiva' },
                { icon: '📊', text: 'Análise de Clientes' },
                { icon: '⚡', text: 'Automação Total' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-full">
                  <span>{item.icon}</span>
                  <span className="text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Visual - Device Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="relative z-10"
            >
              {/* Main device */}
              <div className="bg-slate-800 rounded-[2.5rem] p-3 shadow-2xl max-w-md mx-auto">
                <div className="bg-white rounded-[2rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="flex items-center justify-between px-6 py-3 bg-slate-50">
                    <div className="text-xs font-medium text-slate-400">9:41</div>
                    <div className="flex gap-1">
                      <div className="w-4 h-2 bg-slate-300 rounded-sm" />
                      <div className="w-4 h-2 bg-slate-300 rounded-sm" />
                      <div className="w-6 h-3 bg-green-500 rounded-sm" />
                    </div>
                  </div>

                  {/* App content preview */}
                  <div className="p-6 min-h-[320px] bg-gradient-to-b from-white to-slate-50">
                    {/* Logo AVAK */}
                    <div className="flex items-center justify-center mb-4">
                      <img 
                        src="/team/avak-logo.png" 
                        alt="AVAK" 
                        className="h-10 w-auto"
                      />
                    </div>

                    {/* IA Insights */}
                    <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-xl p-3 mb-4 border border-violet-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm">🧠</span>
                        <span className="text-xs font-semibold text-violet-700">IA Recomenda</span>
                      </div>
                      <p className="text-xs text-slate-600">Aumente o preço do X-Bacon em 8% - demanda alta detectada</p>
                    </div>

                    {/* Mini chart */}
                    <div className="flex items-end gap-2 h-16 mb-4">
                      {[35, 55, 40, 70, 50, 85, 65].map((h, i) => (
                        <motion.div
                          key={i}
                          initial={{ height: 0 }}
                          animate={{ height: `${h}%` }}
                          transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                          className="flex-1 bg-gradient-to-t from-blue-500 to-violet-400 rounded-t-lg"
                        />
                      ))}
                    </div>

                    {/* Quick stats */}
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Vendas', value: 'R$ 8.4k', icon: '💰' },
                        { label: 'Pedidos', value: '47', icon: '📝' },
                        { label: 'Clientes', value: '32', icon: '👥' },
                      ].map((stat, i) => (
                        <div key={i} className="text-center p-2 bg-white rounded-xl shadow-sm">
                          <div className="text-sm mb-1">{stat.icon}</div>
                          <div className="text-sm font-bold text-slate-900">{stat.value}</div>
                          <div className="text-[10px] text-slate-400">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Floating badge - IA */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1, type: 'spring' }}
              className="absolute -right-4 top-20 z-20"
            >
              <div className="bg-gradient-to-r from-violet-500 to-blue-600 rounded-2xl shadow-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <span className="text-xl">🧠</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">IA Ativa</div>
                  <div className="text-xs text-violet-200">Analisando dados</div>
                </div>
              </div>
            </motion.div>

            {/* Floating badge - Economia */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, type: 'spring' }}
              className="absolute -left-4 bottom-32 z-20"
            >
              <div className="bg-white rounded-2xl shadow-xl p-3 flex items-center gap-2 border border-green-100">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-green-600">-30% Desperdício</div>
                  <div className="text-[10px] text-slate-400">IA de Estoque</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Indicador de mais conteúdo - centralizado */}
      <motion.div 
        className="flex justify-center absolute bottom-6 w-full"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
      >
        <button onClick={scrollToNext} className="flex flex-col items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors group focus:outline-none">
          <span className="text-sm font-medium">Role para descobrir</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            className="bg-blue-600 rounded-full p-2 shadow-lg shadow-blue-600/30"
          >
            <ChevronDown className="w-5 h-5 text-white" />
          </motion.div>
        </button>
      </motion.div>
    </section>
  )
}
